import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Modal from "react-bootstrap/Modal";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaArrowLeft,
  FaCalendarCheck,
  FaChartLine,
  FaCheck,
  FaChevronDown,
  FaChevronRight,
  FaChevronUp,
  FaEye,
  FaFileExcel,
  FaHandshake,
  FaListUl,
  FaSearch,
  FaSpinner,
  FaStar,
  FaSyncAlt,
  FaUsers,
} from "react-icons/fa";
import AppContext from "../../contexts/AppContext";
import exportToExcel from "../../services/exportExcel";
import ListaTabelaModal from "../../components/listaTabelaModal";
import "./admPatrocinadoresList.css";
import "../../components/modal/modal.css";

const INDICADORES_VAZIOS = {
  totalAgendado: 0,
  totalReunioesNecessarias: 0,
  participantesPrioritariosEscolhidos: 0,
  totalParticipantesPrioritarios: 0,
  participantesDesejavelsEscolhidos: 0,
  totalParticipantesDesejaveis: 0,
  totalParticipantesEscolhidos: 0,
  totalParticipantes: 0,
  totalEscolhasNecessarias: 0,
  cotaEscolhasPrioritarios: 0,
  cotaEscolhasDesejaveis: 0,
  totalEscolhasRealizadas: 0,
  totalPatrocinadores: 0,
  reunioesPreenchidas: 0,
  totalReunioesPrevistas: 0,
  slotsUtilizados: 0,
  totalSlots: 0,
  capacidadeTotal: 0,
  reunioesSimultaneas: 0,
  reunioesMatchMeet: 0,
  agendamentosMatchMeet: 0,
  maxReunioesSlot: 0,
};

const VISAO_GERAL_VAZIA = {
  totalEscolhasRealizadas: 0,
  totalEscolhasNecessarias: 0,
  totalParticipantesEscolhidos: 0,
  totalParticipantes: 0,
  reunioesPreenchidas: 0,
  totalPatrocinadores: 0,
};

const REUNIOES_MATCH_MEET_COLS = [
  {
    key: "convidadoNome",
    label: "Convidado",
    excelLabel: "Convidado",
  },
  {
    key: "convidadoCargo",
    label: "Cargo",
    excelLabel: "Cargo",
    excelValue: (row) => row.convidadoCargo || "",
    render: (row) =>
      row.convidadoCargo ? (
        row.convidadoCargo
      ) : (
        <span className="ltm-muted">—</span>
      ),
  },
  {
    key: "convidadoEmpresa",
    label: "Empresa",
    excelLabel: "Empresa",
    excelValue: (row) => row.convidadoEmpresa || "",
    render: (row) =>
      row.convidadoEmpresa ? (
        row.convidadoEmpresa
      ) : (
        <span className="ltm-muted">—</span>
      ),
  },
  {
    key: "patrocinadorNome",
    label: "Patrocinador",
    excelLabel: "Patrocinador",
  },
  {
    key: "dataHora",
    label: "Data e horário",
    excelLabel: "Data e horário",
    className: "ltm-col-nowrap",
  },
];

const EXCEL_SLOT_VAGO_STYLE = {
  fill: { patternType: "solid", fgColor: { rgb: "FDE68A" } },
  font: { bold: true, color: { rgb: "92400E" } },
  alignment: { horizontal: "center", vertical: "center" },
};

function tipoLabelSlotMarcacao(tipo) {
  if (tipo === "m_m") return "Match & Meet";
  if (tipo === "reuniao") return "Reunião";
  return "Compromisso";
}

function labelSlotMarcacoes(itens) {
  if (!Array.isArray(itens) || itens.length === 0) return "Livre";
  return itens.map((item) => item.label).filter(Boolean).join("; ") || "Livre";
}

function tipoPrincipalSlot(itens) {
  if (!Array.isArray(itens) || itens.length === 0) return null;
  if (
    itens.some((item) => item.tipo === "m_m") &&
    !itens.some((item) => item.tipo === "reuniao")
  ) {
    return "m_m";
  }
  if (itens.some((item) => item.tipo === "reuniao")) return "reuniao";
  return itens[0]?.tipo || "outro";
}

function fmt(n) {
  return Number(n ?? 0).toLocaleString("pt-BR");
}

function normalizarTextoBusca(valor) {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function pct(num, den) {
  const n = Number(num ?? 0);
  const d = Number(den ?? 0);
  if (!d) return 0;
  return Math.round((n / d) * 100);
}

function patrocinadorTemEscolha(patrocinador) {
  const escolhas =
    patrocinador?.escolhaPatrocinador ??
    patrocinador?.escolha_patrocinador ??
    [];
  return Array.isArray(escolhas) && escolhas.length > 0;
}

function escolhasDoPatrocinadorItem(patrocinador) {
  const escolhas =
    patrocinador?.escolhaPatrocinador ??
    patrocinador?.escolha_patrocinador ??
    [];
  if (!Array.isArray(escolhas)) return [];

  return [...escolhas]
    .map((escolha) => {
      const usuario = escolha?.usuario || {};
      const tipo =
        escolha?.tipo === "mais_quero" || escolha?.tipo === "quero"
          ? escolha.tipo
          : "quero";
      return {
        id: escolha?.id,
        idUsuario: escolha?.idUsuario ?? escolha?.user_id ?? usuario?.id,
        nome: usuario?.nome || "—",
        empresa: usuario?.empresa || "",
        tipo,
        tipoLabel: tipo === "mais_quero" ? "Prioritário" : "Desejável",
        sequencia: Number(escolha?.sequencia ?? 0),
      };
    })
    .sort((a, b) => {
      if (a.tipo !== b.tipo) {
        return a.tipo === "mais_quero" ? -1 : 1;
      }
      return a.sequencia - b.sequencia;
    });
}

function nomePatrocinador(patrocinador) {
  return patrocinador?.patrocinador || patrocinador?.nome || "—";
}

function sequenciaPatrocinador(patrocinador) {
  const valor = Number(patrocinador?.sequencia);
  return Number.isFinite(valor) ? valor : Number.MAX_SAFE_INTEGER;
}

function ordenarPatrocinadoresPorSequencia(lista) {
  return [...(lista || [])].sort((a, b) => {
    const diff = sequenciaPatrocinador(a) - sequenciaPatrocinador(b);
    if (diff !== 0) return diff;
    return nomePatrocinador(a).localeCompare(nomePatrocinador(b), "pt-BR");
  });
}

function cotasEscolhaPatrocinador(patrocinador) {
  const plano = patrocinador?.plano || {};
  return {
    prioritarios: Number(
      plano.escolhaMaximaMaisQuero ?? plano.escolha_maxima_mais_quero ?? 0
    ),
    desejaveis: Number(
      plano.escolhaMaximaQuero ?? plano.escolha_maxima_quero ?? 0
    ),
  };
}

function contagemEscolhasPatrocinador(patrocinador) {
  const escolhas =
    patrocinador?.escolhaPatrocinador ??
    patrocinador?.escolha_patrocinador ??
    [];
  if (!Array.isArray(escolhas)) {
    return { prioritarios: 0, desejaveis: 0 };
  }

  let prioritarios = 0;
  let desejaveis = 0;
  for (const escolha of escolhas) {
    if (escolha?.tipo === "mais_quero") {
      prioritarios += 1;
    } else if (escolha?.tipo === "quero") {
      desejaveis += 1;
    }
  }
  return { prioritarios, desejaveis };
}

function preenchimentoListasPatrocinador(patrocinador) {
  const cotas = cotasEscolhaPatrocinador(patrocinador);
  const contagem = contagemEscolhasPatrocinador(patrocinador);
  return {
    prioritariosPct: pct(contagem.prioritarios, cotas.prioritarios),
    desejaveisPct: pct(contagem.desejaveis, cotas.desejaveis),
    prioritariosFeitos: contagem.prioritarios,
    desejaveisFeitos: contagem.desejaveis,
    prioritariosCota: cotas.prioritarios,
    desejaveisCota: cotas.desejaveis,
  };
}

function listasPatrocinadorFinalizadas(preenchimento) {
  return (
    preenchimento.prioritariosFeitos >= preenchimento.prioritariosCota &&
    preenchimento.desejaveisFeitos >= preenchimento.desejaveisCota
  );
}

function ContagemTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;

  return (
    <div className="adb-chart-tooltip">
      <p className="adb-chart-tooltip-title">{item.label || label}</p>
      <p>
        Quantidade: <strong>{fmt(item.quantidade ?? item.marcacoes)}</strong>
      </p>
      {item.percentual != null ? (
        <p>
          Participação: <strong>{item.percentual}%</strong>
        </p>
      ) : null}
      {item.horario ? (
        <p>
          Horário: <strong>{item.horario}</strong>
        </p>
      ) : null}
    </div>
  );
}

function KpiButton({
  label,
  value,
  hint,
  title,
  icon,
  tone = "blue",
  onClick,
  to,
  actionLabel,
}) {
  const hasFooter = Boolean(onClick || to);
  const className = [
    "adb-kpi",
    "adb-kpi--compact",
    `adb-kpi--tone-${tone}`,
    hasFooter ? "adb-kpi--has-footer" : "",
    onClick ? "adb-kpi--clickable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={className} title={title || undefined}>
      <div className="adb-kpi-body">
        <p className="adb-kpi-label" title={title || undefined}>
          {label}
        </p>
        <div className="adb-kpi-metric">
          <p className="adb-kpi-value">{value}</p>
          <div className="adb-kpi-icon-watermark" aria-hidden>
            {icon}
          </div>
        </div>
        {hint ? <p className="adb-kpi-hint">{hint}</p> : null}
      </div>
      {to ? (
        <Link to={to} className="adb-kpi-footer">
          <span>{actionLabel || "Ver detalhes"}</span>
        </Link>
      ) : onClick ? (
        <button type="button" className="adb-kpi-footer" onClick={onClick}>
          <span>{actionLabel || "Ver detalhes"}</span>
        </button>
      ) : null}
    </article>
  );
}

function ChartCard({
  title,
  subtitle,
  icon,
  children,
  empty,
  emptyMessage = "Sem dados suficientes para este gráfico.",
  className = "",
  actions = null,
  collapsible = false,
  defaultCollapsed = false,
}) {
  const [collapsed, setCollapsed] = useState(Boolean(defaultCollapsed));
  const panelId = useMemo(
    () =>
      `adb-chart-panel-${String(title || "card")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}`,
    [title]
  );

  return (
    <div
      className={`apl-card adb-chart-card adb-stats-card ${className}${
        collapsible && collapsed ? " is-collapsed" : ""
      }`}
    >
      <div className="adb-chart-header">
        <div>
          <h3>
            {icon ? (
              <span className="adb-stats-card-icon" aria-hidden>
                {icon}
              </span>
            ) : null}
            {title}
          </h3>
          {subtitle ? <p className="adb-chart-subtitle">{subtitle}</p> : null}
        </div>
        {(actions || collapsible) ? (
          <div className="adb-chart-header-actions">
            {actions}
            {collapsible ? (
              <button
                type="button"
                className="apl-btn apl-btn--ghost adb-chart-collapse-btn"
                onClick={() => setCollapsed((atual) => !atual)}
                aria-expanded={!collapsed}
                aria-controls={panelId}
                title={collapsed ? "Expandir" : "Recolher"}
              >
                {collapsed ? (
                  <FaChevronDown size={14} aria-hidden />
                ) : (
                  <FaChevronUp size={14} aria-hidden />
                )}
                <span>{collapsed ? "Expandir" : "Recolher"}</span>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {!collapsed ? (
        <div id={panelId}>
          {empty ? <p className="adb-empty-chart">{emptyMessage}</p> : children}
        </div>
      ) : null}
    </div>
  );
}

export default function AdmDashAgendamentos() {
  const { getIndicadoresMatching } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [atualizando, setAtualizando] = useState(false);
  const [indicadores, setIndicadores] = useState(INDICADORES_VAZIOS);
  const [visaoGeral, setVisaoGeral] = useState(VISAO_GERAL_VAZIA);
  const [topParticipantes, setTopParticipantes] = useState([]);
  const [convidadosMatchMeet, setConvidadosMatchMeet] = useState([]);
  const [reunioesMatchMeetLista, setReunioesMatchMeetLista] = useState([]);
  const [modalMatchMeetAberto, setModalMatchMeetAberto] = useState(false);
  const [escolhasDoPatrocinador, setEscolhasDoPatrocinador] = useState([]);
  const [dadosSlots, setDadosSlots] = useState([]);
  const [agendaSlotsPatrocinadores, setAgendaSlotsPatrocinadores] = useState([]);
  const [patrocinadores, setPatrocinadores] = useState([]);
  const [patrocinadorSelecionado, setPatrocinadorSelecionado] = useState("");
  const [modalParticipante, setModalParticipante] = useState(null);
  const [modalPreenchimentoAberto, setModalPreenchimentoAberto] = useState(false);
  const [modalPreenchimentoPatrocinador, setModalPreenchimentoPatrocinador] =
    useState(null);
  const [modalSlotsPatrocinadoresAberto, setModalSlotsPatrocinadoresAberto] =
    useState(false);
  const [buscaRankingParticipantes, setBuscaRankingParticipantes] = useState("");
  const requestIdRef = useRef(0);
  const cargaInicialRef = useRef(true);

  const carregarIndicadores = useCallback(
    async (patrocinadorId = null, { silent = false } = {}) => {
      const requestId = ++requestIdRef.current;
      if (!silent) setLoading(true);
      setAtualizando(true);
      setError("");
      try {
        const dadosAPI = await getIndicadoresMatching(patrocinadorId);
        if (requestId !== requestIdRef.current) return;
        if (!dadosAPI?.dados) {
          throw new Error("Resposta inválida da API");
        }

        setIndicadores({
          ...INDICADORES_VAZIOS,
          ...(dadosAPI.dados.indicadores || {}),
        });

        setVisaoGeral({
          ...VISAO_GERAL_VAZIA,
          ...(dadosAPI.visaoGeral || {}),
        });

        if (Array.isArray(dadosAPI.topParticipantes)) {
          setTopParticipantes(
            [...dadosAPI.topParticipantes].sort(
              (a, b) =>
                (b.totalEscolhas?.length || 0) - (a.totalEscolhas?.length || 0)
            )
          );
        } else {
          setTopParticipantes([]);
        }

        setConvidadosMatchMeet(
          Array.isArray(dadosAPI.convidadosMatchMeet)
            ? dadosAPI.convidadosMatchMeet
            : []
        );
        setReunioesMatchMeetLista(
          Array.isArray(dadosAPI.reunioesMatchMeetLista)
            ? dadosAPI.reunioesMatchMeetLista
            : []
        );
        setEscolhasDoPatrocinador(
          Array.isArray(dadosAPI.escolhasDoPatrocinador)
            ? dadosAPI.escolhasDoPatrocinador
            : []
        );
        setDadosSlots(Array.isArray(dadosAPI.dadosSlots) ? dadosAPI.dadosSlots : []);
        setAgendaSlotsPatrocinadores(
          Array.isArray(dadosAPI.agendaSlotsPatrocinadores)
            ? dadosAPI.agendaSlotsPatrocinadores
            : []
        );
        setPatrocinadores(
          Array.isArray(dadosAPI.patrocinadores) ? dadosAPI.patrocinadores : []
        );
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error(err);
        setError("Não foi possível carregar os indicadores de agendamentos.");
        setReunioesMatchMeetLista([]);
        setAgendaSlotsPatrocinadores([]);
      } finally {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
        setAtualizando(false);
      }
    },
    [getIndicadoresMatching]
  );

  useEffect(() => {
    const silent = !cargaInicialRef.current;
    cargaInicialRef.current = false;
    carregarIndicadores(
      patrocinadorSelecionado === "" ? null : patrocinadorSelecionado,
      { silent }
    );
  }, [carregarIndicadores, patrocinadorSelecionado]);

  const handlePatrocinadorChange = (event) => {
    setPatrocinadorSelecionado(event.target.value);
  };

  const rankingParticipantesFiltrado = useMemo(() => {
    const termo = normalizarTextoBusca(buscaRankingParticipantes);
    const lista = (topParticipantes || []).map((participante, index) => ({
      participante,
      posicao: index + 1,
    }));

    if (!termo) return lista;

    return lista.filter(({ participante }) => {
      const nome = normalizarTextoBusca(participante?.nome);
      const empresa = normalizarTextoBusca(participante?.empresa);
      return nome.includes(termo) || empresa.includes(termo);
    });
  }, [topParticipantes, buscaRankingParticipantes]);

  const exportarRankingParticipantes = useCallback(() => {
    if (!topParticipantes.length) return;

    const dados = topParticipantes.map((participante) => ({
      Nome: participante.nome || "",
      Empresa: participante.empresa || "",
      Cargo: participante.cargo || "",
      "Quantidade de escolhas": participante.totalEscolhas?.length || 0,
      Prioritários: Number(participante.escolhasPrioritarias ?? 0),
      Desejáveis: Number(participante.escolhasDesejaveis ?? 0),
    }));

    const dataRef = new Date().toISOString().slice(0, 10);
    exportToExcel(dados, `ranking_participantes_${dataRef}`);
  }, [topParticipantes]);

  const slotsChartData = useMemo(
    () =>
      (dadosSlots || []).map((slot) => ({
        label: slot.slot || slot.horario || "—",
        horario: slot.horario,
        quantidade: Number(slot.marcacoes ?? 0),
        marcacoes: Number(slot.marcacoes ?? 0),
      })),
    [dadosSlots]
  );

  const colunasAgendaSlotsPatrocinadores = useMemo(() => {
    const cols = [
      {
        key: "patrocinadorNome",
        label: "Patrocinador",
        excelLabel: "Patrocinador",
        className: "ltm-col-nowrap",
      },
    ];

    for (const slot of dadosSlots || []) {
      const hora =
        slot.horaInicio ||
        String(slot.horario || "").split(" - ")[0] ||
        slot.slot ||
        `Slot ${slot.id}`;
      const label = `Slot ${slot.id}${hora ? ` (${hora})` : ""}`;
      cols.push({
        key: `slot_${slot.id}`,
        label,
        excelLabel: label,
        excelValue: (row) => row?.[`slot_${slot.id}`] || "",
        excelEmptyValue: "SLOT VAGO",
        excelEmptyStyle: EXCEL_SLOT_VAGO_STYLE,
        render: (row) => {
          const valor = row?.[`slot_${slot.id}`];
          if (!valor) return <span className="ltm-muted">—</span>;
          return valor;
        },
      });
    }

    return cols;
  }, [dadosSlots]);

  const linhasAgendaSlotsPatrocinadores = useMemo(
    () =>
      (agendaSlotsPatrocinadores || []).map((linha) => ({
        ...linha,
        id: linha.idPatrocinador,
      })),
    [agendaSlotsPatrocinadores]
  );

  const slotsOcupadosAgenda = useMemo(
    () =>
      (dadosSlots || []).filter(
        (slot) => Array.isArray(slot.itens) && slot.itens.length > 0
      ).length,
    [dadosSlots]
  );

  const patrocinadoresEscolheram = useMemo(() => {
    if (!modalParticipante) return [];
    const idParticipante = Number(modalParticipante.id);

    return ordenarPatrocinadoresPorSequencia(
      (patrocinadores || []).filter((p) =>
        modalParticipante.totalEscolhas?.includes(p.id)
      )
    ).map((p) => {
      const escolhas =
        p?.escolhaPatrocinador ?? p?.escolha_patrocinador ?? [];
      const escolhaDoParticipante = Array.isArray(escolhas)
        ? escolhas.find(
            (e) =>
              Number(e?.idUsuario ?? e?.user_id ?? e?.usuario?.id) ===
              idParticipante
          )
        : null;
      const tipo =
        escolhaDoParticipante?.tipo === "mais_quero" ||
        escolhaDoParticipante?.tipo === "quero"
          ? escolhaDoParticipante.tipo
          : null;

      return {
        ...p,
        tipoEscolha: tipo,
        tipoEscolhaLabel:
          tipo === "mais_quero"
            ? "Prioritário"
            : tipo === "quero"
              ? "Desejável"
              : null,
      };
    });
  }, [modalParticipante, patrocinadores]);

  const patrocinadoresOrdenados = useMemo(() => {
    return ordenarPatrocinadoresPorSequencia(patrocinadores);
  }, [patrocinadores]);

  const patrocinadoresComEscolha = useMemo(() => {
    return patrocinadoresOrdenados.filter(patrocinadorTemEscolha);
  }, [patrocinadoresOrdenados]);

  const patrocinadoresModalPreenchimento = patrocinadoresOrdenados;

  const escolhasModalPreenchimento = useMemo(
    () => escolhasDoPatrocinadorItem(modalPreenchimentoPatrocinador),
    [modalPreenchimentoPatrocinador]
  );

  const totalPatrocinadoresMatching = patrocinadores.length;
  const totalPatrocinadoresComEscolha = patrocinadoresComEscolha.length;

  const fecharModalPreenchimento = useCallback(() => {
    setModalPreenchimentoAberto(false);
    setModalPreenchimentoPatrocinador(null);
  }, []);

  const nomePatrocinadorFiltro = useMemo(() => {
    if (!patrocinadorSelecionado) return null;
    const encontrado = patrocinadores.find(
      (p) => String(p.id) === String(patrocinadorSelecionado)
    );
    return encontrado?.patrocinador || encontrado?.nome || null;
  }, [patrocinadorSelecionado, patrocinadores]);

  const escolhasPorUsuarioId = useMemo(() => {
    const mapa = new Map();
    for (const escolha of escolhasDoPatrocinador || []) {
      if (escolha?.idUsuario == null) continue;
      mapa.set(Number(escolha.idUsuario), escolha);
    }
    return mapa;
  }, [escolhasDoPatrocinador]);

  const kpisVisaoGeral = [
    {
      id: "cobertura",
      label: "Cobertura de participantes",
      value: `${pct(
        visaoGeral.totalParticipantesEscolhidos,
        visaoGeral.totalParticipantes
      )}%`,
      hint: `${fmt(visaoGeral.totalParticipantesEscolhidos)} de ${fmt(
        visaoGeral.totalParticipantes
      )} participantes`,
      title:
        "Percentual de participantes (convidados) únicos que foram escolhidos por ao menos um patrocinador no matching.",
      icon: <FaUsers size={18} />,
      tone: "blue",
    },
    {
      id: "preenchimento",
      label: "Taxa de preenchimento",
      value: `${pct(
        totalPatrocinadoresComEscolha,
        totalPatrocinadoresMatching
      )}%`,
      hint: `${fmt(totalPatrocinadoresComEscolha)} de ${fmt(
        totalPatrocinadoresMatching
      )} patrocinadores`,
      title:
        "Percentual de patrocinadores que já realizaram ao menos uma escolha de participantes no matching.",
      icon: <FaChartLine size={18} />,
      tone: "amber",
      onClick: () => setModalPreenchimentoAberto(true),
      actionLabel: "Ver patrocinadores",
    },
  ];

  const kpisPorPatrocinador = [
    {
      id: "escolhidos-por-tipo",
      label: "Participantes escolhidos",
      value: `${fmt(indicadores.totalParticipantesPrioritarios)} / ${fmt(
        indicadores.totalParticipantesDesejaveis
      )}`,
      hint: "Prioritários / Desejáveis",
      title:
        "Total de escolhas de participantes no matching, separando prioritários (mais quero) e desejáveis (quero).",
      icon: <FaUsers size={18} />,
      tone: "blue",
    },
    {
      id: "preenchimento-listas",
      label: "Preenchimento das listas",
      value: `${pct(
        indicadores.totalParticipantesPrioritarios,
        indicadores.cotaEscolhasPrioritarios
      )}% / ${pct(
        indicadores.totalParticipantesDesejaveis,
        indicadores.cotaEscolhasDesejaveis
      )}%`,
      hint: `${fmt(indicadores.totalParticipantesPrioritarios)}/${fmt(
        indicadores.cotaEscolhasPrioritarios
      )} prioritários · ${fmt(indicadores.totalParticipantesDesejaveis)}/${fmt(
        indicadores.cotaEscolhasDesejaveis
      )} desejáveis`,
      title:
        "Percentual de preenchimento das cotas de escolha do plano: prioritários (mais quero) e desejáveis (quero).",
      icon: <FaListUl size={18} />,
      tone: "amber",
    },
    {
      id: "prioritarios",
      label: "Participantes prioritários",
      value: `${pct(
        indicadores.participantesPrioritariosEscolhidos,
        indicadores.totalAgendado
      )}%`,
      hint: `${fmt(indicadores.participantesPrioritariosEscolhidos)} de ${fmt(
        indicadores.totalAgendado
      )} reuniões da lista prioritária`,
      title:
        "Percentual das reuniões de negócios agendadas que vieram da lista prioritária (mais quero).",
      icon: <FaStar size={18} />,
      tone: "green",
    },
    {
      id: "desejaveis",
      label: "Participantes desejáveis",
      value: `${pct(
        indicadores.participantesDesejavelsEscolhidos,
        indicadores.totalAgendado
      )}%`,
      hint: `${fmt(indicadores.participantesDesejavelsEscolhidos)} de ${fmt(
        indicadores.totalAgendado
      )} reuniões da lista desejável`,
      title:
        "Percentual das reuniões de negócios agendadas que vieram da lista desejável (quero).",
      icon: <FaEye size={18} />,
      tone: "slate",
    },
    {
      id: "simultaneas",
      label: "Reuniões simultâneas",
      value: fmt(indicadores.reunioesSimultaneas),
      hint: "Máximo por slot de horário",
      title:
        "Capacidade máxima de reuniões simultâneas por horário, somando as cotas dos planos dos patrocinadores (ou do filtro selecionado).",
      icon: <FaUsers size={18} />,
      tone: "slate",
    },
  ];

  return (
    <section
      className="adb-section adb-tab-panel"
      id="adb-panel-agendamentos"
      role="tabpanel"
      aria-labelledby="adb-tab-agendamentos"
    >
      <div className="adb-agend-toolbar">
        <div className="adb-agend-toolbar-text">
          <h3 className="adb-section-title">Indicadores de matching</h3>
          <p className="adb-agend-toolbar-hint">
            Acompanhe preenchimento, rankings e utilização da agenda.
          </p>
        </div>
        <div className="adb-agend-toolbar-actions">
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() =>
              carregarIndicadores(
                patrocinadorSelecionado === "" ? null : patrocinadorSelecionado,
                { silent: true }
              )
            }
            disabled={loading || atualizando}
          >
            {atualizando ? (
              <FaSpinner className="fa-spin" size={14} aria-hidden />
            ) : (
              <FaSyncAlt size={14} aria-hidden />
            )}
            Atualizar
          </button>
        </div>
      </div>

      {error ? (
        <div className="adb-error-banner" role="alert">
          <p>{error}</p>
          <button
            type="button"
            className="apl-btn apl-btn--secondary"
            onClick={() =>
              carregarIndicadores(
                patrocinadorSelecionado === "" ? null : patrocinadorSelecionado
              )
            }
          >
            Tentar novamente
          </button>
        </div>
      ) : null}

      {loading && !error ? (
        <div className="apl-loading" aria-live="polite">
          <FaSpinner className="fa-spin" size={28} aria-hidden />
          <p>Carregando agendamentos…</p>
        </div>
      ) : null}

      {!loading && !error ? (
        <>
          <div className="adb-agend-kpi-block">
            <div className="adb-agend-kpi-block-header">
              <h4 className="adb-agend-kpi-block-title">Visão geral</h4>
            </div>
            <div className="adb-kpi-grid adb-kpi-grid--2 adb-kpi-grid--compact-row adb-agend-kpi-grid">
              {kpisVisaoGeral.map((kpi) => (
                <KpiButton key={kpi.id} {...kpi} />
              ))}
            </div>
            <ChartCard
              title="Ranking de participantes"
              subtitle="Mais escolhidos pelos patrocinadores"
              icon={<FaUsers size={14} />}
              empty={topParticipantes.length === 0}
              className="adb-agend-ranking-card adb-agend-ranking-card--geral"
              collapsible
              defaultCollapsed
              actions={
                <>
                  <label className="adb-agend-ranking-search">
                    <FaSearch size={12} aria-hidden />
                    <input
                      type="search"
                      value={buscaRankingParticipantes}
                      onChange={(event) =>
                        setBuscaRankingParticipantes(event.target.value)
                      }
                      placeholder="Buscar nome ou empresa"
                      aria-label="Pesquisar participante por nome ou empresa"
                      disabled={topParticipantes.length === 0}
                    />
                  </label>
                  <button
                    type="button"
                    className="apl-btn apl-btn--ghost adb-chart-export-btn"
                    onClick={exportarRankingParticipantes}
                    disabled={topParticipantes.length === 0}
                    title="Exportar ranking em Excel"
                  >
                    <FaFileExcel size={14} aria-hidden />
                    Exportar Excel
                  </button>
                </>
              }
            >
              {rankingParticipantesFiltrado.length === 0 ? (
                <p className="adb-empty-chart">
                  Nenhum participante encontrado para “{buscaRankingParticipantes.trim()}”.
                </p>
              ) : (
                <ol className="adb-agend-ranking-list">
                  {rankingParticipantesFiltrado.map(
                    ({ participante, posicao }) => (
                      <li key={participante.id || posicao}>
                        <button
                          type="button"
                          className="adb-agend-ranking-item adb-agend-ranking-item--clickable"
                          onClick={() => setModalParticipante(participante)}
                          title="Ver empresas que escolheram este participante"
                        >
                          <span
                            className={`adb-agend-rank-num ${
                              posicao <= 3 ? "adb-agend-rank-num--top" : ""
                            }`}
                          >
                            {posicao}
                          </span>
                          <div className="adb-agend-rank-info">
                            <strong>
                              <span className="adb-agend-rank-nome">
                                {participante.nome}
                              </span>
                              <span
                                className="adb-agend-rank-tipo-count"
                                title={`${fmt(participante.escolhasPrioritarias ?? 0)} prioritário(s) · ${fmt(participante.escolhasDesejaveis ?? 0)} desejável(is)`}
                              >
                                {fmt(participante.escolhasPrioritarias ?? 0)} /{" "}
                                {fmt(participante.escolhasDesejaveis ?? 0)}
                              </span>
                            </strong>
                            <span>{participante.empresa || "—"}</span>
                          </div>
                          <span className="adb-agend-rank-count adb-agend-rank-count--static">
                            {participante.totalEscolhas?.length || 0}
                          </span>
                        </button>
                      </li>
                    )
                  )}
                </ol>
              )}
            </ChartCard>
          </div>

          <div className="adb-agend-kpi-block">
            <div className="adb-agend-kpi-block-header adb-agend-kpi-block-header--with-actions">
              <div>
                <h4 className="adb-agend-kpi-block-title">Visão por patrocinador</h4>
                <p className="adb-agend-kpi-block-hint">
                  {nomePatrocinadorFiltro
                    ? `Filtrado por ${nomePatrocinadorFiltro}.`
                    : "Sem filtro: soma de todos os patrocinadores."}
                </p>
              </div>
              <select
                className="adb-agend-select"
                value={patrocinadorSelecionado}
                onChange={handlePatrocinadorChange}
                disabled={loading || atualizando}
                aria-label="Filtrar visão por patrocinador"
              >
                <option value="">Todos os patrocinadores</option>
                {patrocinadoresOrdenados.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.patrocinador || p.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="adb-kpi-grid adb-kpi-grid--compact-row adb-agend-kpi-grid adb-agend-kpi-grid--por-patrocinador">
              {kpisPorPatrocinador.map((kpi) => (
                <KpiButton key={kpi.id} {...kpi} />
              ))}
            </div>
          </div>

          <div className="adb-stats-grid adb-stats-grid--top adb-agend-main-grid">
            <ChartCard
              title="Marcações por slot"
              subtitle="Distribuição de reuniões de negócios por horário"
              icon={<FaChartLine size={14} />}
              empty={slotsChartData.length === 0}
              actions={
                <button
                  type="button"
                  className="apl-btn apl-btn--ghost adb-chart-export-btn"
                  onClick={() => setModalSlotsPatrocinadoresAberto(true)}
                  disabled={
                    loading ||
                    atualizando ||
                    !linhasAgendaSlotsPatrocinadores.length ||
                    !dadosSlots.length
                  }
                  title="Ver e baixar agenda de slots por patrocinador"
                >
                  <FaFileExcel size={14} aria-hidden />
                  Baixar slots
                </button>
              }
            >
              <div className="adb-stats-vbar-wrap" role="img" aria-label="Marcações por slot">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={slotsChartData}
                    margin={{ top: 8, right: 8, left: 0, bottom: 48 }}
                    barCategoryGap="18%"
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#e2e8f0"
                    />
                    <XAxis
                      dataKey="label"
                      interval={0}
                      angle={-28}
                      textAnchor="end"
                      height={60}
                      tick={{ fill: "#64748b", fontSize: 11, fontWeight: 600 }}
                      axisLine={{ stroke: "#e2e8f0" }}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: "#94a3b8", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                    />
                    <Tooltip content={<ContagemTooltip />} cursor={{ fill: "rgba(13, 29, 65, 0.04)" }} />
                    <Bar dataKey="quantidade" radius={[6, 6, 0, 0]} maxBarSize={40} fill="#0d1d41" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard
              title="Utilização de agenda"
              subtitle="Ocupação e capacidade com base nos slots reais"
              icon={<FaCalendarCheck size={14} />}
            >
              <dl className="adb-agend-details">
                <div className="adb-agend-detail-row">
                  <dt title="Quantidade de horários com ao menos uma reunião de negócios agendada.">
                    Slots utilizados
                  </dt>
                  <dd>
                    {fmt(indicadores.slotsUtilizados)}/{fmt(indicadores.totalSlots)}
                  </dd>
                </div>
                <div className="adb-agend-detail-row">
                  <dt title="Reuniões de negócios agendadas em relação à cota de reuniões do plano do patrocinador (ou soma das cotas, sem filtro).">
                    Reuniões agendadas
                  </dt>
                  <dd>
                    {fmt(indicadores.totalAgendado)}/{fmt(indicadores.totalReunioesNecessarias)}
                  </dd>
                </div>
                <div className="adb-agend-detail-row">
                  <dt title="Percentual de conclusão da cota de reuniões do plano.">
                    Taxa de ocupação
                  </dt>
                  <dd>
                    {pct(indicadores.totalAgendado, indicadores.totalReunioesNecessarias)}%
                  </dd>
                </div>
                <div
                  className="adb-agend-detail-row adb-agend-detail-row--clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => setModalMatchMeetAberto(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setModalMatchMeetAberto(true);
                    }
                  }}
                  title="Ver reuniões Match & Meet agendadas"
                  aria-label="Abrir lista de reuniões Match & Meet agendadas"
                >
                  <dt title="Escolhas de convidados no Match & Meet e reuniões do tipo m_m já registradas na agenda.">
                    Match & Meet
                  </dt>
                  <dd className="adb-agend-detail-dd-stack">
                    <span>
                      {fmt(indicadores.reunioesMatchMeet)}{' '}
                      {Number(indicadores.reunioesMatchMeet) === 1
                        ? 'escolha'
                        : 'escolhas'}
                    </span>
                    <span
                      className="adb-agend-detail-sub"
                      title="Reuniões do tipo Match & Meet (m_m) registradas na agenda."
                    >
                      {fmt(indicadores.agendamentosMatchMeet)}{' '}
                      {Number(indicadores.agendamentosMatchMeet) === 1
                        ? 'reunião'
                        : 'reuniões'}
                    </span>
                  </dd>
                </div>
              </dl>
            </ChartCard>
          </div>

          <ChartCard
            title={
              nomePatrocinadorFiltro
                ? `Agenda de ${nomePatrocinadorFiltro}`
                : "Visão por slots"
            }
            subtitle={
              nomePatrocinadorFiltro
                ? "Marcações do patrocinador por horário"
                : "Selecione um patrocinador no filtro para ver a agenda por slots"
            }
            icon={<FaCalendarCheck size={14} />}
            className="adb-agend-slots-card"
            empty={!patrocinadorSelecionado || dadosSlots.length === 0}
            emptyMessage={
              !patrocinadorSelecionado
                ? "Selecione um patrocinador no filtro para visualizar as marcações por slot."
                : "Nenhum slot cadastrado."
            }
          >
            <div className="adb-agend-slots">
              <div className="adb-agend-slots-head">
                <h4>Agenda do patrocinador</h4>
                <span className="adb-agend-slots-hint">
                  {fmt(slotsOcupadosAgenda)} de {fmt(dadosSlots.length)} slots
                  ocupados
                </span>
              </div>
              <div className="adb-agend-slots-grid">
                {dadosSlots.map((slot) => {
                  const itens = Array.isArray(slot.itens) ? slot.itens : [];
                  const tipo = tipoPrincipalSlot(itens);
                  const hora =
                    slot.horaInicio ||
                    String(slot.horario || "").split(" - ")[0] ||
                    "";
                  return (
                    <article
                      key={slot.id}
                      className={`adb-agend-slot-card${
                        itens.length > 0
                          ? ` is-${tipo || "outro"}`
                          : " is-livre"
                      }`}
                      title={
                        itens.length > 0
                          ? itens
                              .map(
                                (item) =>
                                  `${item.label}${
                                    item.empresa ? ` (${item.empresa})` : ""
                                  } · ${tipoLabelSlotMarcacao(item.tipo)}`
                              )
                              .join("\n")
                          : "Slot disponível"
                      }
                    >
                      <header>
                        <span className="adb-agend-slot-id">Slot {slot.id}</span>
                        {hora ? (
                          <span className="adb-agend-slot-hora">{hora}</span>
                        ) : null}
                      </header>
                      {itens.length > 1 ? (
                        <ul className="adb-agend-slot-labels">
                          {itens.map((item, itemIndex) => (
                            <li key={`${slot.id}-${item.idUsuario || itemIndex}`}>
                              {item.label}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="adb-agend-slot-label">
                          {labelSlotMarcacoes(itens)}
                        </p>
                      )}
                      <span className="adb-agend-slot-tipo">
                        {itens.length === 0
                          ? "Disponível"
                          : itens.length > 1
                            ? `${itens.length} marcações`
                            : tipoLabelSlotMarcacao(tipo)}
                      </span>
                    </article>
                  );
                })}
              </div>
            </div>
          </ChartCard>

          <div className="adb-stats-grid adb-agend-bottom-grid">
            <ChartCard
              title="Match & Meet"
              subtitle={
                nomePatrocinadorFiltro
                  ? `Convidados que escolheram ${nomePatrocinadorFiltro}`
                  : "Selecione um patrocinador no filtro para ver os convidados"
              }
              icon={<FaHandshake size={14} />}
              empty={!patrocinadorSelecionado || convidadosMatchMeet.length === 0}
              emptyMessage={
                !patrocinadorSelecionado
                  ? "Selecione um patrocinador no filtro para listar os convidados."
                  : "Nenhum convidado escolheu este patrocinador no Match & Meet."
              }
            >
              <ol className="adb-agend-ranking-list">
                {convidadosMatchMeet.map((convidado, index) => {
                  const escolhaDoPatrocinador = escolhasPorUsuarioId.get(
                    Number(convidado.id)
                  );

                  return (
                    <li key={convidado.id || index} className="adb-agend-ranking-item">
                      <span
                        className={`adb-agend-rank-num ${
                          index < 3 ? "adb-agend-rank-num--top" : ""
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="adb-agend-rank-info">
                        <strong>{convidado.nome}</strong>
                        <span>{convidado.empresa || "—"}</span>
                      </div>
                      {convidado.agendado ? (
                        <span
                          className="adb-agend-rank-check"
                          title="Reunião Match & Meet já agendada com este patrocinador"
                          aria-label="Match & Meet agendado"
                        >
                          <FaCheck size={11} aria-hidden />
                        </span>
                      ) : null}
                      {escolhaDoPatrocinador ? (
                        <span
                          className={`adb-agend-rank-badge ${
                            escolhaDoPatrocinador.tipo === "mais_quero"
                              ? "adb-agend-rank-badge--prioritario"
                              : "adb-agend-rank-badge--desejavel"
                          }`}
                          title="Incluído na lista de escolhas do patrocinador"
                        >
                          {escolhaDoPatrocinador.tipoLabel}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </ChartCard>

            <ChartCard
              title="Escolhas do patrocinador"
              subtitle={
                nomePatrocinadorFiltro
                  ? `Participantes escolhidos por ${nomePatrocinadorFiltro}`
                  : "Selecione um patrocinador no filtro para ver as escolhas"
              }
              icon={<FaListUl size={14} />}
              empty={!patrocinadorSelecionado || escolhasDoPatrocinador.length === 0}
              emptyMessage={
                !patrocinadorSelecionado
                  ? "Selecione um patrocinador no filtro para listar as escolhas."
                  : "Este patrocinador ainda não fez escolhas no matching."
              }
            >
              <ol className="adb-agend-ranking-list">
                {escolhasDoPatrocinador.map((escolha, index) => (
                  <li key={escolha.id || index} className="adb-agend-ranking-item">
                    <span
                      className={`adb-agend-rank-num ${
                        escolha.tipo === "mais_quero"
                          ? "adb-agend-rank-num--top"
                          : ""
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="adb-agend-rank-info">
                      <strong>{escolha.nome}</strong>
                      <span>{escolha.empresa || "—"}</span>
                    </div>
                    {escolha.agendado ? (
                      <span
                        className="adb-agend-rank-check"
                        title="Reunião já agendada com este patrocinador"
                        aria-label="Reunião agendada"
                      >
                        <FaCheck size={11} aria-hidden />
                      </span>
                    ) : null}
                    <span
                      className={`adb-agend-rank-badge ${
                        escolha.tipo === "mais_quero"
                          ? "adb-agend-rank-badge--prioritario"
                          : "adb-agend-rank-badge--desejavel"
                      }`}
                    >
                      {escolha.tipoLabel}
                    </span>
                  </li>
                ))}
              </ol>
            </ChartCard>
          </div>
        </>
      ) : null}

      <Modal
        show={Boolean(modalParticipante)}
        onHide={() => setModalParticipante(null)}
        centered
        size="md"
        dialogClassName="adb-agend-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Empresas que escolheram {modalParticipante?.nome}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {patrocinadoresEscolheram.length > 0 ? (
            <ul className="adb-agend-modal-list">
              {patrocinadoresEscolheram.map((p, index) => (
                <li key={p.id || index}>
                  <span className="adb-agend-modal-num">{index + 1}</span>
                  <span className="adb-agend-modal-name">
                    {nomePatrocinador(p)}
                  </span>
                  {p.tipoEscolhaLabel ? (
                    <span
                      className={`adb-agend-rank-badge ${
                        p.tipoEscolha === "mais_quero"
                          ? "adb-agend-rank-badge--prioritario"
                          : "adb-agend-rank-badge--desejavel"
                      }`}
                    >
                      {p.tipoEscolhaLabel}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-0 text-muted">
              Nenhuma empresa escolheu este participante ainda.
            </p>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={modalPreenchimentoAberto}
        onHide={fecharModalPreenchimento}
        centered
        size="md"
        scrollable
        dialogClassName="adb-agend-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalPreenchimentoPatrocinador
              ? `Escolhas — ${nomePatrocinador(modalPreenchimentoPatrocinador)}`
              : "Patrocinadores do matching"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalPreenchimentoPatrocinador ? (
            <>
              <button
                type="button"
                className="adb-agend-modal-back"
                onClick={() => setModalPreenchimentoPatrocinador(null)}
              >
                <FaArrowLeft size={12} aria-hidden />
                Voltar para patrocinadores
              </button>
              {escolhasModalPreenchimento.length > 0 ? (
                <ul className="adb-agend-modal-list">
                  {escolhasModalPreenchimento.map((escolha, index) => (
                    <li key={escolha.id || index}>
                      <span className="adb-agend-modal-num">{index + 1}</span>
                      <div className="adb-agend-modal-info">
                        <span className="adb-agend-modal-name">{escolha.nome}</span>
                        {escolha.empresa ? (
                          <span className="adb-agend-modal-meta">
                            {escolha.empresa}
                          </span>
                        ) : null}
                      </div>
                      <span
                        className={`adb-agend-rank-badge ${
                          escolha.tipo === "mais_quero"
                            ? "adb-agend-rank-badge--prioritario"
                            : "adb-agend-rank-badge--desejavel"
                        }`}
                      >
                        {escolha.tipoLabel}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mb-0 text-muted">
                  Este patrocinador ainda não possui escolhas.
                </p>
              )}
            </>
          ) : patrocinadoresModalPreenchimento.length > 0 ? (
            <>
              <p className="adb-agend-modal-summary">
                {fmt(totalPatrocinadoresComEscolha)} de{" "}
                {fmt(totalPatrocinadoresMatching)} patrocinadores com escolha
              </p>
              <ul className="adb-agend-modal-list">
                {patrocinadoresModalPreenchimento.map((p, index) => {
                  const preenchimento = preenchimentoListasPatrocinador(p);
                  const finalizado = listasPatrocinadorFinalizadas(preenchimento);
                  const labelPreenchimento = `${preenchimento.prioritariosPct}% / ${preenchimento.desejaveisPct}%`;

                  return (
                    <li key={p.id || index}>
                      <button
                        type="button"
                        className="adb-agend-modal-row"
                        onClick={() => setModalPreenchimentoPatrocinador(p)}
                        title={
                          finalizado
                            ? `Listas finalizadas — ver escolhas de ${nomePatrocinador(p)}`
                            : `Listas não finalizadas — ver escolhas de ${nomePatrocinador(p)}`
                        }
                      >
                        <span className="adb-agend-modal-num">{index + 1}</span>
                        <span className="adb-agend-modal-name">
                          {nomePatrocinador(p)}
                        </span>
                        <span
                          className="adb-agend-modal-pct"
                          title={`Preenchimento das listas: ${preenchimento.prioritariosFeitos}/${preenchimento.prioritariosCota} prioritários · ${preenchimento.desejaveisFeitos}/${preenchimento.desejaveisCota} desejáveis`}
                        >
                          {labelPreenchimento}
                        </span>
                        {finalizado ? (
                          <span
                            className="adb-agend-modal-check"
                            title="Listas finalizadas (100% / 100%)"
                            aria-hidden
                          >
                            <FaCheck size={11} />
                          </span>
                        ) : (
                          <span
                            className="adb-agend-modal-check adb-agend-modal-check--pending"
                            title="Listas não finalizadas"
                          >
                            —
                          </span>
                        )}
                        <FaChevronRight
                          className="adb-agend-modal-chevron"
                          size={12}
                          aria-hidden
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            <p className="mb-0 text-muted">
              Nenhum patrocinador encontrado no matching.
            </p>
          )}
        </Modal.Body>
      </Modal>

      <ListaTabelaModal
        show={modalMatchMeetAberto}
        onClose={() => setModalMatchMeetAberto(false)}
        title="Reuniões Match & Meet"
        subtitle={
          nomePatrocinadorFiltro
            ? `Agendamentos de Match & Meet filtrados por ${nomePatrocinadorFiltro}.`
            : "Todas as reuniões do tipo Match & Meet registradas na agenda."
        }
        countLabel="Reuniões"
        columns={REUNIOES_MATCH_MEET_COLS}
        rows={reunioesMatchMeetLista}
        loading={loading}
        error={error || null}
        emptyMessage="Nenhuma reunião Match & Meet agendada."
        loadingMessage="Carregando reuniões Match & Meet…"
        excelFileName={`match_meet_reunioes_${new Date().toISOString().slice(0, 10)}`}
        excelSheetName="Match & Meet"
        getRowHref={(row) =>
          row.convidadoId
            ? `/private/adm/convidados/editar/${row.convidadoId}`
            : null
        }
        size="xl"
      />

      <ListaTabelaModal
        show={modalSlotsPatrocinadoresAberto}
        onClose={() => setModalSlotsPatrocinadoresAberto(false)}
        title="Slots por patrocinador"
        subtitle={
          nomePatrocinadorFiltro
            ? `Agenda de slots de ${nomePatrocinadorFiltro} (convidado — empresa).`
            : "Matriz de agenda: uma linha por patrocinador e uma coluna por slot (convidado — empresa)."
        }
        countLabel="Patrocinadores"
        columns={colunasAgendaSlotsPatrocinadores}
        rows={linhasAgendaSlotsPatrocinadores}
        loading={loading}
        error={error || null}
        emptyMessage="Nenhum patrocinador ou slot disponível para montar a lista."
        loadingMessage="Carregando agenda de slots…"
        excelFileName={`slots_patrocinadores_${new Date().toISOString().slice(0, 10)}`}
        excelSheetName="Slots"
        size="xl"
        dialogClassName="ltm-dialog--wide"
      />
    </section>
  );
}
